import {
  Box,
  Button,
  Text
} from '@chakra-ui/react';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuButton, MenuList, SearchInput } from '@morressier/alembic';
import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  value: string;
  options: string[];
  placeholder?: string;
  noDataText?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onSelect: (value: string) => void;
};

const FieldAutoSuggestSelect: React.FC<Props> = ({
  value,
  options,
  placeholder = 'Select field',
  noDataText = 'No fields found',
  isDisabled = false,
  isInvalid = false,
  onSelect
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter(option =>
      option.toLowerCase().includes(normalizedSearch)
    );
  }, [options, searchTerm]);

  useEffect(() => {
    if (!buttonRef.current) return;
    const { width } = buttonRef.current.getBoundingClientRect();
    setMenuWidth(width);
  }, []);

  const handleClose = () => {
    setMenuOpen(false);
    setSearchTerm('');
  };

  return (
    <Menu
      ariaLabel="field suggest menu"
      isOpen={menuOpen}
      onClose={handleClose}
    >
      <MenuButton
        as={Button}
        variant={'outline'}
        rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        textAlign="left"
        borderColor={isInvalid ? 'errorPrimaryBorder' : 'neutralSecondaryFill'}
        ref={buttonRef}
        disabled={isDisabled}
        aria-invalid={isInvalid}
        onClick={() => setMenuOpen(prevValue => !prevValue)}
      >
        <Text
          textStyle="textBody"
          isTruncated
        >
          {value || placeholder}
        </Text>
      </MenuButton>

      <MenuList
        maxH="300px"
        overflowY="auto"
        p={0}
        width={menuWidth ? `${menuWidth}px` : 'auto'}
      >
        <Box
          position="sticky"
          top="0"
          bg="surfacePrimaryFill"
          zIndex="1"
          p={2}
          borderBottom="1px solid"
          borderColor="neutralSecondaryFill"
        >
          <SearchInput
            placeholder="Search field"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            onInputClear={() => setSearchTerm('')}
            name="field-autosuggest-search"
          />
        </Box>

        {filteredOptions.length ? (
          <Box>
            {filteredOptions.map(option => (
              <Box
                key={option}
                p={3}
                _hover={{ bg: 'actionSecondaryFill', cursor: 'pointer' }}
                onClick={() => {
                  onSelect(option);
                  handleClose();
                }}
              >
                <Text textStyle="textContext" fontSize="lg">
                  {option}
                </Text>
              </Box>
            ))}
          </Box>
        ) : (
          <Box p={2}>
            <Text>{noDataText}</Text>
          </Box>
        )}
      </MenuList>
    </Menu>
  );
};

export default FieldAutoSuggestSelect;
